/**
 * The chart block a model may emit, and what this platform will accept.
 *
 * ## Why the model emits a structure rather than us inferring one
 *
 * The alternative was reading numbers back out of a table the model wrote and guessing which
 * column is a series and which is a label. That guess is wrong on the first table with a footnote
 * in it, and when it is wrong it draws a chart of the wrong numbers — a failure that looks
 * authoritative. Asking for the shape means the model says what it meant, and anything it does not
 * say is a chart that does not render.
 *
 * ## A chart is a factual claim
 *
 * Every number in it has to come from a source, exactly as a sentence does. The prompt says so;
 * `cite` carries the source numbers; and the renderer shows them, because a chart with no
 * attribution is the most quotable unattributed claim in the product — an employee will photograph
 * it.
 *
 * ## Validation refuses rather than repairs
 *
 * A malformed block renders nothing and leaves the fence as text. Not an error to the employee:
 * they still have the answer, and a chart is an extra. What must never happen is a half-parsed
 * chart with a missing series drawn as though it were complete.
 */

/** Two types, for the reason the Button has three variants. */
export type ChartType = "bar" | "line";

export interface ChartSeries {
  label: string;
  values: number[];
}

export interface ChartSpec {
  type: ChartType;
  title: string;
  /** Appended to every value in the legend and the accessible table. "LYD", "%", "days". */
  unit: string;
  /** The x axis: fee channels, months, account tiers. */
  categories: string[];
  series: ChartSeries[];
  /** Source numbers backing the figures, as they appear in the answer's `[n]` markers. */
  cite: number[];
}

/** Beyond this a bar chart is a table someone has made harder to read. */
const MAX_CATEGORIES = 12;
const MAX_SERIES = 4;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Parse one chart block, or null.
 *
 * Null for anything unexpected — a missing field, a series whose length does not match the
 * categories, a value that is not a number. The caller leaves the text alone when this returns
 * null, so a model that emits nonsense costs the employee nothing but the chart.
 */
export function parseChartSpec(source: string): ChartSpec | null {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null) return null;

  const candidate = raw as Record<string, unknown>;
  const type = candidate.type;
  if (type !== "bar" && type !== "line") return null;

  const categories = candidate.categories;
  if (
    !Array.isArray(categories) ||
    categories.length === 0 ||
    categories.length > MAX_CATEGORIES ||
    !categories.every((entry) => typeof entry === "string")
  ) {
    return null;
  }

  const series = candidate.series;
  if (!Array.isArray(series) || series.length === 0 || series.length > MAX_SERIES) {
    return null;
  }

  const parsed: ChartSeries[] = [];
  for (const entry of series) {
    if (typeof entry !== "object" || entry === null) return null;
    const row = entry as Record<string, unknown>;
    if (typeof row.label !== "string") return null;
    // Every series must cover every category. A short one would otherwise draw bars against the
    // wrong labels, which is worse than not drawing it.
    if (
      !Array.isArray(row.values) ||
      row.values.length !== categories.length ||
      !row.values.every(isFiniteNumber)
    ) {
      return null;
    }
    parsed.push({ label: row.label, values: row.values as number[] });
  }

  const cite = Array.isArray(candidate.cite)
    ? candidate.cite.filter(isFiniteNumber).map((value) => Math.trunc(value))
    : [];

  return {
    type,
    title: typeof candidate.title === "string" ? candidate.title : "",
    unit: typeof candidate.unit === "string" ? candidate.unit : "",
    categories: categories as string[],
    series: parsed,
    cite,
  };
}
